var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useContext, useEffect, useMemo, useState } from "react";
import { lookupNestedProp, TableContext } from "./Table";
import { EditContext } from "./EditProvider";
import { useSnackbar } from "../Snackbar";
import { TablePersonalizeContext } from "./TablePersonalize";
import { selectMany } from "nate-react-api-helpers";
import { useSyncedRef } from "../SyncedRef";
import { getEditable, getEditKey } from "./TableEditCell";
import { Button, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { Dialog2 } from "../Dialog2";
import { applyPastedRow, PasteError, pasteErrorSummary, pasteRowLabel } from "./PasteRow";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const blankString = "<blank_string_nnnnnnnnnnnn>";
export function RangeActionProvider(props) {
    const { reload } = useContext(TableContext);
    const { onRange, onSelect, hasPopout, transition } = useContext(EditContext);
    const personalize = useContext(TablePersonalizeContext);
    const onChangeRef = useSyncedRef(props.onChange);
    const reloadRef = useSyncedRef(reload);
    const dataRef = useSyncedRef(selectMany(props.data, p => p.rows));
    const snack = useSnackbar();
    const snackRef = useSyncedRef(snack);
    const [hasRange, setHasRange] = useState(false);
    const [hasSelection, setHasSelection] = useState(false);
    useEffect(() => { const sub = onRange.subscribeAndFireLast(v => setHasRange(!!v)); return () => sub.cancel(); }, [onRange]);
    useEffect(() => { const sub = onSelect.subscribeAndFireLast(v => setHasSelection(!!v && !v.editing)); return () => sub.cancel(); }, [onSelect]);
    const getTableColumns = personalize.getTableColumns;
    const columns = useMemo(() => getTableColumns(props.columns), [props.columns, getTableColumns]);
    const columnsRef = useSyncedRef(columns.columns);
    const [showReload, setShowReload] = useState(false);
    useEffect(() => {
        if (hasPopout)
            return;
        if (!hasRange && !hasSelection)
            return;
        const remove = (e) => __awaiter(this, void 0, void 0, function* () {
            const isKeyCombination = e.key === "Backspace" || e.key === "Delete";
            if (!isKeyCombination)
                return;
            const changer = onChangeRef.current;
            if (!changer)
                return;
            let range = onRange.lastValue;
            if (!range) {
                if (!onSelect.lastValue)
                    return;
                range = { ids: [onSelect.lastValue.id], fromId: onSelect.lastValue.id, toId: onSelect.lastValue.id, toIndex: onSelect.lastValue.index, fromIndex: onSelect.lastValue.index };
            }
            try {
                const startIndex = Math.min(range.fromIndex, range.toIndex);
                const endIndex = Math.max(range.fromIndex, range.toIndex);
                const columns = columnsRef.current.filter((v, index) => index >= startIndex && index <= endIndex);
                if (columns.length === 1 && range.ids.length === 1) {
                    return; // ignore b/c this is handled by single-cell editing.
                }
                const blanks = columns.map(() => "");
                const errors = [];
                const rows = []; // @ts-ignore
                dataRef.current.filter(v => range.ids.indexOf(v.id) !== -1).forEach((p, index) => { try {
                    rows.push(applyPastedRow(columns, p, blanks, pasteRowLabel(p, index)));
                }
                catch (e) {
                    if (!(e instanceof PasteError))
                        throw e;
                    errors.push(e);
                } });
                if (errors.length > 0) {
                    snackRef.current.error(pasteErrorSummary(errors));
                    return;
                }
                yield submitRows(onChangeRef.current, rows);
            }
            catch (err) {
                setShowReload(true);
            }
        });
        document.body.addEventListener("keydown", remove);
        return () => { document.body.removeEventListener("keydown", remove); };
    }, [columnsRef, dataRef, hasSelection, hasRange, onChangeRef, onRange, onSelect, reloadRef, snackRef, hasPopout]);
    useEffect(() => {
        if (hasPopout)
            return;
        if (!hasRange && !hasSelection)
            return;
        const copy = e => {
            const isKeyCombination = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "c";
            if (!isKeyCombination)
                return;
            let range = onRange.lastValue;
            if (!range) {
                if (!onSelect.lastValue)
                    return;
                range = { ids: [onSelect.lastValue.id], fromId: onSelect.lastValue.id, toId: onSelect.lastValue.id, toIndex: onSelect.lastValue.index, fromIndex: onSelect.lastValue.index };
            } // @ts-ignore
            let rows = dataRef.current.filter(v => range.ids.indexOf(v.id) !== -1);
            const minIndex = Math.min(range.fromIndex, range.toIndex);
            const maxIndex = Math.max(range.fromIndex, range.toIndex);
            const cols = columnsRef.current.filter((v, index) => index >= minIndex && index <= maxIndex);
            const tabSeparated = rows.map(r => {
                return cols.map(c => {
                    const editKey = getEditKey(c, r);
                    const editable = getEditable(c, r);
                    if (editable !== null && editable !== void 0 && editable.copy)
                        return editable.copy(r);
                    if (c.copy)
                        return c.copy(r); // @ts-ignore
                    if (editKey)
                        return lookupNestedProp(r, editKey);
                    if ((editable === null || editable === void 0 ? void 0 : editable.type) === "button")
                        return "";
                    if (c.render) {
                        const result = c.render(r, c);
                        if (typeof result === "string")
                            return result;
                    }
                    return "";
                }).map(v => v || blankString).join("\t");
            }).join("\n");
            e.preventDefault();
            navigator.clipboard.writeText(tabSeparated).then(() => { snackRef.current.success("Copied selection to clipboard"); }, err => { snackRef.current.error(err.toString()); });
        };
        document.body.addEventListener("keydown", copy);
        return () => { document.body.removeEventListener("keydown", copy); };
    }, [columnsRef, hasSelection, dataRef, hasRange, onRange, onSelect, snackRef, hasPopout]);
    useEffect(() => {
        if (hasPopout)
            return;
        if (!hasSelection && !hasRange)
            return;
        const paste = (e) => __awaiter(this, void 0, void 0, function* () {
            const isKeyCombination = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "v";
            if (!isKeyCombination)
                return;
            const changer = onChangeRef.current;
            if (!changer)
                return;
            e.preventDefault();
            try {
                const value = yield navigator.clipboard.readText();
                let parts = value.trim().split("\n").map(v => v.replace(/\r$/g, ""));
                const range = onRange.lastValue;
                const selection = onSelect.lastValue;
                let startId = 0;
                let startIndex = 0;
                let endIndex = 0;
                if (range) {
                    startId = range.ids[0];
                    startIndex = Math.min(range.fromIndex, range.toIndex);
                    endIndex = Math.max(range.fromIndex, range.toIndex);
                }
                else if (selection) {
                    startId = selection.id;
                    startIndex = selection.index;
                    endIndex = selection.index;
                }
                const columns = columnsRef.current.filter((v, index) => index >= startIndex); // @ts-ignore
                let dataIndex = dataRef.current.findIndex(v => v.id === startId);
                if (dataIndex === -1) {
                    throw new Error("Invalid start position");
                }
                const destinationRowCount = range ? range.ids.length : 1;
                if (parts.length === 1 && destinationRowCount > 1) {
                    parts = Array(destinationRowCount).fill(parts[0]);
                }
                let lastId = 0;
                const errors = [];
                const items = [];
                parts.forEach((p, rowIndex) => { if (dataRef.current.length <= dataIndex)
                    return; const cells = p.split("\t").map(v => v === blankString ? "" : v); const src = dataRef.current[dataIndex]; dataIndex++; lastId = src.id; try {
                    items.push(applyPastedRow(columns, src, cells, pasteRowLabel(src, rowIndex)));
                }
                catch (e) {
                    if (!(e instanceof PasteError))
                        throw e;
                    errors.push(e);
                } }); // the whole paste is rejected if any row fails, nothing is sent
                if (errors.length > 0) {
                    snackRef.current.error(pasteErrorSummary(errors));
                    return;
                }
                onSelect.emit({ id: lastId, index: endIndex, value: null, editing: false });
                transition("down");
                yield submitRows(onChangeRef.current, items);
            }
            catch (err) {
                setShowReload(true);
            }
        });
        document.body.addEventListener("keydown", paste);
        return () => { document.body.removeEventListener("keydown", paste); };
    }, [hasSelection, hasRange, snackRef, onSelect, onRange, columnsRef, dataRef, onChangeRef, reloadRef, hasPopout, transition]);
    if (showReload) {
        const submit = () => { window.location.reload(); };
        return /*#__PURE__*/ _jsxs(Dialog2, { open: true, onEnterPress: submit, children: [/*#__PURE__*/ _jsx(DialogTitle, { children: "Reload Page to Continue" }), /*#__PURE__*/ _jsx(DialogContent, { children: /*#__PURE__*/ _jsx(DialogContentText, { children: "An error has occurred while pasting data. Please reload the page to continue." }) }), /*#__PURE__*/ _jsx(DialogActions, { children: /*#__PURE__*/ _jsx(Button, { variant: "contained", onClick: submit, children: "Reload" }) })] });
    }
    return null;
} // submitRows sends the rows to the table's updater. The updater reports its own failures to the
// user and restores the rows (see EditProvider.updateBulk), so a rejection here is not an error
// that needs the reload dialog.
function submitRows(onChange, rows) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            yield onChange(rows);
        }
        catch (e) { // already reported by the updater
        }
    });
}
